import { Component, EventEmitter, Injector, Input, OnChanges, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { CountByFilter, LoanStatus, PipelineFilter, Role, UserRolesFilter, UserRolesFilterUser } from 'src/app/models';
import { EnumerationService } from 'src/app/services/enumeration-service';
import { ApplicationContextBoundComponent } from 'src/app/shared/components';
import * as _ from 'lodash';
import { User } from 'src/app/models/user/user.model';
import { PipelineService } from 'src/app/services/pipeline.service';
import { FilterOption, FilterOptionBadge, GenericFilterComponent, ShowMoreFilterOptions } from 'src/app/shared/components/generic-filter/generic-filter.component';
import { LoanPurpose } from 'src/app/models/config/loan-purpose.model';
import { EnumerationItem } from 'src/app/models/simple-enum-item.model';
import { ChannelService } from 'src/app/services/channel.service';

@Component({
  selector: 'tpo-pipeline-filter',
  templateUrl: 'tpo-pipeline-filter.component.html',
  styleUrls: ['tpo-pipeline-filter.component.scss'],
  standalone: false
})

export class TpoPipelineFilterComponent extends ApplicationContextBoundComponent implements OnInit, OnChanges {

  @ViewChildren("filter")
  filters: QueryList<GenericFilterComponent>;

  @ViewChild("loanStatusFilter")
  loanStatusFilter: GenericFilterComponent;

  @ViewChild("channelFilter")
  channelFilter: GenericFilterComponent;

  @ViewChild("loanPurposeFilter")
  loanPurposeFilter: GenericFilterComponent;

  @Input()
  activeFilter: UserRolesFilter = new UserRolesFilter();

  @Input()
  pipelineFiltersList: PipelineFilter[] = [];

  @Input()
  activeFilterName: string;

  @Input()
  disableFilters: boolean;

  @Output()
  pipelineFilterChanged: EventEmitter<UserRolesFilter> = new EventEmitter<UserRolesFilter>();

  @Output()
  filterSectionVisibleChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  addEditPipelineFilterClicked: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  selectPipelineFilterClicked: EventEmitter<number> = new EventEmitter<number>();

  @Output()
  deletePipelineFilterClicked: EventEmitter<number> = new EventEmitter<number>();

  @Output()
  tableSearchReset: EventEmitter<never> = new EventEmitter<never>();

  @Output()
  stageGroupChanged: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  applyFiltersClicked: EventEmitter<UserRolesFilter> = new EventEmitter<UserRolesFilter>();

  hasPendingFilterChanges: boolean = false;

  filterOptions: FilterOption[] = [];

  label: string = "";

  @Input()
  selectedStageGroup: string = "Processing";

  // When on, the pipeline shows Archived / Withdrawn / Denied loans (statuses flagged showAsArchive)
  // instead of the normal stage-group statuses.
  isShowAsArchiveActive: boolean = false;

  // Loan-status filter snapshot taken when entering archive view, restored when leaving it.
  private _loanStatusesBeforeArchive: number[] | null = null;

  // Guards the one-time "scope the filter to the default stage group on load" step.
  private _initialStageScopeApplied: boolean = false;

  loanStageGroups: string[] = [];

  statusesToFilterWith: FilterOption[] = [];

  officersToFilterWith: FilterOption[] = [];

  channelsToFilterWith: FilterOption[] = [];

  loanPurposesToFilterWith: FilterOption[] = [];

  roles: Role[] = [];

  otherRoles: Role[] = [];

  users: any[] = [];

  userFilterOptionsByRoleId: Map<number, FilterOption[]> = new Map<number, FilterOption[]>();

  showMoreRolesOptions: Record<number, ShowMoreFilterOptions> = {}
  primaryRoleId: number;

  hideLoSelector: boolean = true;
  hideChannelsSelector: boolean = true;

  private _loanStatuses: LoanStatus[] = [];

  private _loanPurposes: LoanPurpose[] = [];

  private _channels: EnumerationItem[] = [];

  private _allUsersInCompany: User[] = [];
  private _allUsersMap: Map<string, User> = new Map();

  private _allRoles: Role[] = [];

  private _applicationCountsByFilterCriteria: CountByFilter;

  private _applicationCountsByLoanStatus: { [key: number]: number } = {};

  constructor(
    private readonly _enumerationService: EnumerationService,
    private readonly _pipelineService: PipelineService,
    private readonly _channelService: ChannelService,

    private readonly injector: Injector
  ) {
    super(injector);
    this.loanStageGroups = [...new Set(this._enumerationService.loanStages.map(item => item.groupName))];
    this.loanStageGroups.splice(0, 0, 'All');
  }

  ngOnInit() {
    // Use applicationContext from base class - it's already subscribed and managed
    const context = this.applicationContext;

    // TPO users only see TPO-enabled roles
    this._allRoles = context.globalConfig.roles
      .filter((r) => r.tpoEnabled)
      .sort((r1, r2) => r1.order - r2.order);

    this._allUsersInCompany = context.globalConfig.usersAll.concat(context.globalConfig.tpoUsers);
    this._allUsersInCompany.forEach(u => this._allUsersMap.set(u.userCompanyGuid, u));
    this._channels = context.globalConfig.enabledChannels;
    this._loanPurposes = context.globalConfig.loanPurpose;
    this._loanStatuses = context.globalConfig.loanStatus.sort((a, b) => a.order - b.order);

    this.hideChannelsSelector = context.globalConfig.enabledChannels.length === 0;

    this.primaryRoleId = context.globalConfig.firstRole.roleId;

    // Restore the archived toggle from a previously-saved filter, if any.
    this.isShowAsArchiveActive = !!this.activeFilter?.isShowAsArchiveActive;

    // Scope the (shared-by-reference) filter to the default stage group up front, so the parent's
    // single initial pipeline load is already scoped — no second fetch. If activeFilter hasn't been
    // bound yet, ngOnChanges will run this once it arrives (still before the parent's load).
    this.applyInitialStageScope();

    this.initializeFilters();
  }

  ngOnChanges(): void {
    // activeFilter may be bound asynchronously by the parent (after webPreferences load); apply the
    // default stage-group scope the first time it (and the loan-status config) are available.
    this.applyInitialStageScope();
  }

  // One-time, on load: build the loan-status list for the resolved stage group and write it onto
  // activeFilter so the pipeline opens scoped to that stage (matching the highlighted radio) rather
  // than "all". Mutates the filter object in place — the parent holds the same reference and reads
  // it for its initial load — so this needs no extra emit / API call.
  private applyInitialStageScope = () => {
    if (this._initialStageScopeApplied || !this.activeFilter || this._loanStatuses.length === 0) {
      return;
    }
    this._initialStageScopeApplied = true;
    this.isShowAsArchiveActive = !!this.activeFilter.isShowAsArchiveActive;
    // Pass isInit so any loan statuses restored onto activeFilter (e.g. from a saved view) are
    // marked selected and preserved, instead of being reset to the full stage group.
    this.loadLoanStatusesToFilterWith(true);
    this.updateActiveFilterLoanStatuses();
  }

  onStageGroupChanged = () => {
    this.activeFilter.loanStatuses = [];
    this.statusesToFilterWith = [];

    this.loadLoanStatusesToFilterWith();
    this.updateActiveFilterLoanStatuses();

    this.loadRoleAndUserFilters();
    this.refreshCounts();

    this.hasPendingFilterChanges = false;
    this.stageGroupChanged.emit(this.selectedStageGroup);
    this.applyFiltersClicked.emit(this.activeFilter);
  }

  onShowArchivedToggleChanged = () => {
    if (this.isShowAsArchiveActive) {
      // Going into archive view: remember the loan-status filter so we can put it back,
      // then clear it (the archive scope is driven by isShowAsArchiveActive, not statuses).
      this._loanStatusesBeforeArchive = [...(this.activeFilter.loanStatuses ?? [])];
      this.activeFilter.loanStatuses = [];
    } else {
      // Leaving archive view: restore the loan-status filter exactly as it was. (Do NOT rebuild
      // it from the selected stage group — that would silently narrow the pipeline.)
      this.activeFilter.loanStatuses = this._loanStatusesBeforeArchive ? [...this._loanStatusesBeforeArchive] : [];
      this._loanStatusesBeforeArchive = null;
    }
    this.activeFilter.isShowAsArchiveActive = this.isShowAsArchiveActive;

    this.statusesToFilterWith = [];
    this.loadLoanStatusesToFilterWith();
    this.markSelectedLoanStatusOptions();

    this.loadRoleAndUserFilters();
    this.refreshCounts();

    this.hasPendingFilterChanges = false;
    this.applyFiltersClicked.emit(this.activeFilter);
  }

  // Sync the loan-status quick-filter UI with whatever is currently in activeFilter.loanStatuses.
  private markSelectedLoanStatusOptions = () => {
    const selected = new Set(this.activeFilter.loanStatuses ?? []);
    this.statusesToFilterWith.forEach(o => o.isSelected = selected.has(o.value));
    this.statusesToFilterWith = _.cloneDeep(this.statusesToFilterWith);
  }

  clearAll = () => {
    this.selectedStageGroup = "Processing";
    this.isShowAsArchiveActive = false;
    this._loanStatusesBeforeArchive = null;
    this.stageGroupChanged.emit(this.selectedStageGroup);
    this.tableSearchReset.emit();

    this.activeFilter = {
      loanPurposes: [],
      loanStatuses: [],
      loanSubstatuses: [],
      users: [],
      channels: [],
      branches: [],
      lenders: [],
      states: [],
      roleOrderIdToShowInContacts: null,
      keyDateRange: {
        endDate: null,
        keyDateConfigurationId: null,
        startDate: null,
        displayName: null
      }
    };

    this.pipelineFiltersList.forEach(item => item.filters = new UserRolesFilter());

    this.filters.toArray().forEach(f => {
      f.clear();
    });

    for (const [key, value] of this.userFilterOptionsByRoleId) {
      value.forEach(u => u.isSelected = false);
      this.userFilterOptionsByRoleId.set(key, value)
    }

    this.loadLoanStatusesToFilterWith();
    this.updateActiveFilterLoanStatuses();
    this.loadRoleAndUserFilters();
    this.hasPendingFilterChanges = false;
    this.applyFiltersClicked.emit(this.activeFilter);
    this.refreshCounts();

    // update filters - for 'select all' ui
    this.officersToFilterWith = [...this.officersToFilterWith];
  }

  changeFilter = (filter: UserRolesFilter) => {
    this.activeFilter = filter;
    this.pipelineFilterChanged.emit(this.activeFilter);
    this.refreshCounts();
  }

  onStatusFilterChanged = (selectedOptions: FilterOption[]) => {
    const loanStatusIds = selectedOptions.map(o => o.value);
    this.activeFilter.loanStatuses = loanStatusIds;

    this.updateActiveFilterLoanStatuses();

    this.hasPendingFilterChanges = true;
  }

  onChannelsFilterChanged = (selectedOptions: FilterOption[]) => {
    const channelIds = selectedOptions.map(o => o.value);
    this.activeFilter.channels = channelIds;

    // filter users that belong to selected channels
    this.filterFilterUsersByChannel(channelIds);

    this.hasPendingFilterChanges = true;
  }

  onLoanPurposeFilterChanged = (selectedOptions: FilterOption[]) => {
    const loanPurposeIds = selectedOptions.map(o => o.value);
    this.activeFilter.loanPurposes = loanPurposeIds;
    this.hasPendingFilterChanges = true;
  }

  onUserFilterChanged = (selectedOptions: FilterOption[], roleId: number) => {
    const userRoleIds = selectedOptions.map(o => o.value);

    this.activeFilter.users = [
      ...this.activeFilter.users.filter(u => u.roleId !== roleId),
      ...userRoleIds
    ];

    this.hasPendingFilterChanges = true;
  }

  onCheckChanged = () => {

  }

  onCreateFilterClicked = () => {

  }

  addEditPipelineFilter = (action: string, index: number) => {
    const object = {
      action: action,
      index: index
    }
    this.addEditPipelineFilterClicked.emit(object);
  }

  selectPipelineFilter = (index: number) => {
    this.selectPipelineFilterClicked.emit(index);
  }

  deletePipelineFilter = (index: number) => {
    this.deletePipelineFilterClicked.emit(index);
  }

  applyFilters = () => {
    this.hasPendingFilterChanges = false;
    this.loadRoleAndUserFilters();
    this.refreshCounts();
    this.applyFiltersClicked.emit(this.activeFilter);
  }

  private isLoanStatusSelected = (loanStatus: LoanStatus) => {
    let isSelected = false;
    this.pipelineFiltersList.forEach((pipelineFilters) => {
      if (pipelineFilters.filters.loanStatuses.findIndex(x => x == loanStatus.loanStatusId) > -1) {
        isSelected = true;
      }
    });
    return isSelected && !loanStatus.showAsArchive;
  }

  private loadLoanStatusesToFilterWith = (isInit?: boolean) => {
    if (this.isShowAsArchiveActive) {
      this.statusesToFilterWith = _.cloneDeep(
        this._loanStatuses
          .filter(ls => ls.showAsArchive)
          .map(ls => ({
            displayName: ls.loanStatusName,
            value: ls.loanStatusId,
            groupName: 'Archived',
            badge: this._applicationCountsByLoanStatus[ls.loanStatusId],
            isSelected: false,
          }))
      );
      return;
    }
    // Loan statuses persisted on the bound filter (e.g. restored from a saved view) drive the
    // selected state — TPO has no pipelineFiltersList, so activeFilter is the source of truth.
    const selectedLoanStatusIds = new Set<number>(this.activeFilter?.loanStatuses ?? []);

    if (this.selectedStageGroup === "All") {
      this.statusesToFilterWith = this._loanStatuses.map(ls => ({
        displayName: ls.loanStatusName,
        value: ls.loanStatusId,
        groupName: 'All',
        badge: this._applicationCountsByLoanStatus[ls.loanStatusId],
        isSelected: isInit ? (selectedLoanStatusIds.has(ls.loanStatusId) || this.isLoanStatusSelected(ls)) : false,
      }));
    } else {
      const loanStages = this._enumerationService.loanStages
        .filter(lg => lg.groupName === this.selectedStageGroup)
        .map(ls => ls.value);

      const loanStatuses = this._loanStatuses
        .filter(ls => loanStages.includes(ls.loanStage));

      // Treat "every status in the group is selected" as no explicit selection (i.e. the whole group).
      const groupSelectedCount = loanStatuses.filter(ls => selectedLoanStatusIds.has(ls.loanStatusId)).length;
      const isWholeGroupSelected = groupSelectedCount === loanStatuses.length;

      this.statusesToFilterWith = loanStatuses.map(ls => ({
        displayName: ls.loanStatusName,
        value: ls.loanStatusId,
        groupName: this.selectedStageGroup,
        badge: this._applicationCountsByLoanStatus[ls.loanStatusId],
        isSelected: isInit && !isWholeGroupSelected && (selectedLoanStatusIds.has(ls.loanStatusId) || this.isLoanStatusSelected(ls)),
      }));
    }

    this.statusesToFilterWith = _.cloneDeep(this.statusesToFilterWith);
  }

  private updateLoanStatusCountsOnLoanStatusFilter = () => {
    let updatedCountsPerStatusId: FilterOptionBadge[] = [];

    if (this.selectedStageGroup === "All" && !this.isShowAsArchiveActive) {
      updatedCountsPerStatusId = this._loanStatuses.filter(ls => !ls.showAsArchive).map(ls => {
        const count = this._applicationCountsByFilterCriteria?.byLoanStatus.find(
          (c) => c.filterCriteriaId == ls.loanStatusId
        );
        const countToSet = count && count.total ? count.total : '';
        return new FilterOptionBadge(ls.loanStatusId, countToSet.toString());
      });
    } else {
      updatedCountsPerStatusId = this._loanStatuses.map(ls => {
        const count = this._applicationCountsByFilterCriteria?.byLoanStatus.find(
          (c) => c.filterCriteriaId == ls.loanStatusId
        );
        const countToSet = count && count.total ? count.total : '';
        return new FilterOptionBadge(ls.loanStatusId, countToSet.toString());
      });
    }

    this.loanStatusFilter.updateBadges(updatedCountsPerStatusId);
  }

  private updateChannelCountsOnChannelFilter = () => {
    const updatedCountsPerChannelId: FilterOptionBadge[] =
      this._channels.map(channel => {
        const count = this._applicationCountsByFilterCriteria?.byChannel.find(
          (c) => c.filterCriteriaId == channel.value
        );
        const countToSet = count && count.total ? count.total : '';
        return new FilterOptionBadge(channel.value, countToSet.toString());
      });
    this.channelFilter.updateBadges(updatedCountsPerChannelId);
  }

  private updateLoanPurposeCountsOnLoanPurposeFilter = () => {
    const updatedCountsPerLoanPurposeId: FilterOptionBadge[] =
      this._loanPurposes.map(purpose => {
        const count = this._applicationCountsByFilterCriteria?.byLoanPurpose.find(
          (lp) => lp.filterCriteriaId == purpose.loanPurposeId
        );
        const countToSet = count && count.total ? count.total : '';
        return new FilterOptionBadge(purpose.loanPurposeId, countToSet.toString());
      });
    this.loanPurposeFilter.updateBadges(updatedCountsPerLoanPurposeId);
  }

  private loadChannelsToFilterWith = () => {
    const channelCountMap = new Map<number, any>();
    this._applicationCountsByFilterCriteria.byChannel?.forEach(c => channelCountMap.set(Number(c.filterCriteriaId), c));

    const activeChannelIds = new Set<any>();
    (this.activeFilter?.channels ?? []).forEach(id => activeChannelIds.add(id));
    this.pipelineFiltersList.forEach(pf => {
      if (pf.active) pf.filters.channels.forEach(id => activeChannelIds.add(id));
    });

    const channelFilterOptions: FilterOption[] = [];
    this._channels.forEach((channel) => {
      const count = channelCountMap.get(channel.value);
      const countToSet = count && count.total ? count.total : '';
      const isSelected = activeChannelIds.has(channel.value);
      const option = { displayName: channel.name, value: channel.value, groupName: 'Channel', badge: countToSet, isSelected: isSelected }
      channelFilterOptions.push(option);
    });
    this.channelsToFilterWith = channelFilterOptions;
  }

  private loadLoanPurposesToFilterWith = () => {
    const purposeCountMap = new Map<number, any>();
    this._applicationCountsByFilterCriteria.byLoanPurpose?.forEach(c => purposeCountMap.set(Number(c.filterCriteriaId), c));

    const activePurposeIds = new Set<number>();
    (this.activeFilter?.loanPurposes ?? []).forEach(id => activePurposeIds.add(id));
    this.pipelineFiltersList.forEach(pf => {
      if (pf.active) pf.filters.loanPurposes.forEach(id => activePurposeIds.add(id));
    });

    const loanPurposeFilterOptions: FilterOption[] = [];
    this._loanPurposes.forEach((lp) => {
      const count = purposeCountMap.get(lp.loanPurposeId);
      const countToSet = count && count.total ? count.total : '';
      const isSelected = activePurposeIds.has(lp.loanPurposeId);
      const option = { displayName: lp.loanPurposeName, value: lp.loanPurposeId, groupName: 'Loan Purpose', badge: countToSet, isSelected: isSelected };
      loanPurposeFilterOptions.push(option);
    });
    this.loanPurposesToFilterWith = loanPurposeFilterOptions;
  }

  private loadRoleAndUserFilters = () => {

    const roleIds = new Set<number>();
    const users = [];
    this.roles = [];
    this.otherRoles = [];

    this._pipelineService.getDistinctUserRoles(this.activeFilter).subscribe((usersGroupedByRole) => {
      usersGroupedByRole.forEach((roleUserGroup) => {
        roleUserGroup.userAppCounts.forEach((user) => {
          if (user.userId == '') {
            users.push({
              id: '',
              firstName: '',
              lastName: '-Unassigned',
              roleId: roleUserGroup.roleId,
              active: false,
              unassigned: true,
              appCount: user.appCount,
              displayName: '-Unassigned'
            });
            roleIds.add(roleUserGroup.roleId);
          } else {
            const userDetails = this._allUsersMap.get(user.userId);
            if (userDetails) {
              users.push({
                id: userDetails.userCompanyGuid,
                roleId: roleUserGroup.roleId,
                active: userDetails.active,
                appCount: user.appCount,
                displayName: userDetails.lastName + ', ' + userDetails.firstName + (userDetails.active ? '' : ' - (inactive)')
              });
              roleIds.add(roleUserGroup.roleId);
            }
          }
        });
      });

      const selectedChannels = new Set(this.channelsToFilterWith.filter(c => c.isSelected).map(c => c.value as number));
      const rolesForChannel = selectedChannels.size === 0 ? this._allRoles : this._allRoles.filter(r => r.roleChannels?.some(rc => selectedChannels.has(this._channelService.getChannelsNumber([rc.channel]))));

      rolesForChannel.forEach(role => {
        if (roleIds.has(role.roleId)) {
          this.roles.push(role);
          if (role.roleId !== this.primaryRoleId) {
            this.otherRoles.push(role);
          }

          this.showMoreRolesOptions[role.roleId] = {
            label: `Inactive ${role.roleName}s`,
            baseOptionsFilter: { "active": true },
            opositeOptionsFilter: { "active": false },
            allowSelectAll: true
          }
        }
      });

      for (let i = this.otherRoles.length - 1; i >= 0; i--) {
        if (!roleIds.has(this.otherRoles[i].roleId)) {
          this.otherRoles.splice(i, 1);
        }
      }

      // Only the current external company's TPO users; internal usersAll must not leak into TPO filters.
      const externalCompanyId = this.applicationContext.userPermissions?.externalCompanyId;
      const externalCompanyUsers = externalCompanyId
        ? this.applicationContext.globalConfig.tpoUsers.filter(u => u.externalCompanyId === externalCompanyId)
        : [];

      const existingUserKeys = new Set(users.map(u => u.roleId + '|' + u.id));
      externalCompanyUsers.forEach(user => {
        if (!user.roleId) return;
        if (!existingUserKeys.has(user.roleId + '|' + user.userCompanyGuid)) {
          users.push({
            id: user.userCompanyGuid,
            roleId: user.roleId,
            active: user.active,
            total: null,
            displayName: `${user.lastName}${user.firstName ? ", " + user.firstName : ""} ${user.active ? "" : " - (inactive)"}`,
            isChecked: false,
          });
          existingUserKeys.add(user.roleId + '|' + user.userCompanyGuid);
        }
      })

      const filteredList = users.map(user => new FilterOption({
        displayName: user.displayName,
        value: new UserRolesFilterUser(user.roleId, user.id),
        groupName: "Loan Officer",
        badge: user.appCount,
        active: user.active === 1
      }));

      for (let i = this.activeFilter.users.length - 1; i >= 0; i--) {
        const selectedUser = this.activeFilter.users[i];
        const item = filteredList.find(i => i.value.userId == selectedUser.userId && i.value.roleId === selectedUser.roleId);
        if (!item) {
          this.activeFilter.users.splice(i, 1);
        } else {
          item.isSelected = true;
        }
      }

      this.hideLoSelector = !rolesForChannel.find(r => r.roleId === this.primaryRoleId);
      this.officersToFilterWith = this.hideLoSelector ? [] : filteredList.filter(f => f.value.roleId == this.primaryRoleId);

      const rolesOtherThanLoanOfficer = this._allRoles.filter(r => r.roleId !== this.primaryRoleId);
      this.userFilterOptionsByRoleId.clear();
      rolesOtherThanLoanOfficer.forEach(role => {
        const usersInRole = filteredList.filter(u => u.value.roleId === role.roleId);
        usersInRole.forEach(fo => fo.groupName = role.roleName);
        this.userFilterOptionsByRoleId.set(role.roleId, usersInRole);
      });

    })
  };

  private setApplicationCountsByLoanStatus = () => {
    if (this.isShowAsArchiveActive) {
      this._loanStatuses.filter(ls => ls.showAsArchive).forEach((loanStatus) => {
        const count = this._applicationCountsByFilterCriteria.byLoanStatus.find(
          (c) => c.filterCriteriaId == loanStatus.loanStatusId
        );
        this._applicationCountsByLoanStatus[loanStatus.loanStatusId] = count ? count.total : 0;
      });
    } else if (this.selectedStageGroup === "All") {
      this._loanStatuses.filter(ls => !ls.showAsArchive).forEach((loanStatus) => {
        const count = this._applicationCountsByFilterCriteria.byLoanStatus.find(
          (c) => c.filterCriteriaId == loanStatus.loanStatusId
        );
        const countToSet = count ? count.total : 0;
        this._applicationCountsByLoanStatus[loanStatus.loanStatusId] = countToSet;
      });
    } else {
      this._loanStatuses.forEach((loanStatus) => {
        const count = this._applicationCountsByFilterCriteria.byLoanStatus.find(
          (c) => c.filterCriteriaId == loanStatus.loanStatusId
        );
        const countToSet = count ? count.total : 0;
        this._applicationCountsByLoanStatus[loanStatus.loanStatusId] = countToSet;
      });
    }
  };

  private refreshCounts = () => {
    this._pipelineService
      .getCountByFilter(this.activeFilter, this.isShowAsArchiveActive)
      .subscribe((filteredResults) => {
        this._applicationCountsByFilterCriteria = filteredResults;
        this.updateLoanStatusCountsOnLoanStatusFilter();
        this.updateChannelCountsOnChannelFilter();
        this.updateLoanPurposeCountsOnLoanPurposeFilter();
      });
  }

  private initializeFilters = () => {
    this.loadRoleAndUserFilters();
    this._pipelineService
      .getCountByFilter(this.activeFilter, this.isShowAsArchiveActive)
      .subscribe((filteredResults) => {
        this._applicationCountsByFilterCriteria = filteredResults;
        this.setApplicationCountsByLoanStatus();

        this.loadChannelsToFilterWith();
        this.loadLoanPurposesToFilterWith();

        this.pipelineFiltersList.forEach((pipelineFilters) => {
          if (pipelineFilters.active) {
            if (pipelineFilters.stageGroup) {
              this.selectedStageGroup = pipelineFilters.stageGroup;
            }
          }
        });

        // Rebuild the loan-status list now that badge counts are in, and re-derive the active
        // statuses for the resolved stage group. The filter was already scoped on the (shared)
        // activeFilter object by applyInitialStageScope(), so the parent's single initial load
        // picks it up by reference — no emit / extra API call needed here.
        this.loadLoanStatusesToFilterWith(true);
        this.updateActiveFilterLoanStatuses();
      });
  }

  private updateActiveFilterLoanStatuses = () => {
    if (this.statusesToFilterWith.some(ls => ls.isSelected)) {
      this.activeFilter.loanStatuses = this.statusesToFilterWith
        .filter(ls => ls.isSelected)
        .map(ls => ls.value);
    } else {
      if (this.selectedStageGroup === "All") {
        this.activeFilter.loanStatuses = [];
      } else {
        this.activeFilter.loanStatuses = this.statusesToFilterWith
          .map(ls => ls.value);
      }
    }
  }

  private filterFilterUsersByChannel = (channelIds: number[]) => {
    if (channelIds.length === 0) return;

    const rolesForChannel = this._allRoles.filter(r => r.roleChannels?.some(rc => channelIds.indexOf(this._channelService.getChannelsNumber([rc.channel])) > -1));
    this.activeFilter.users = this.activeFilter.users.filter(u => rolesForChannel.findIndex(r => r.roleId == u.roleId) > -1);
  }
}
